import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import MediaCard from "@/components/base/MediaCard";
import { mediaList } from "@/mocks/media";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useFavorites } from "@/hooks/useFavorites";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const item = useMemo(() => mediaList.find((m) => m.id === id), [id]);
  const [expanded, setExpanded] = useState(false);
  const [toast, setToast] = useState("");
  const { isFavorite, toggle } = useFavorites();

  useDocumentTitle(
    item ? `${item.title} - 光棍影院` : "影视详情 - 光棍影院",
    item
      ? `${item.title}剧情简介：${item.synopsis.slice(0, 70)}… 主演 ${item.cast.join("、")}，导演 ${item.director}。`
      : undefined
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    setExpanded(false);
  }, [id]);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(""), 2200);
  };

  const related = useMemo(() => {
    if (!item) return [];
    const sameType = mediaList.filter((m) => m.id !== item.id && m.type === item.type);
    const scored = sameType.map((m) => {
      const genreHit = m.genre.filter((g) => item.genre.includes(g)).length;
      const directorHit = m.director === item.director ? 2 : 0;
      return { m, score: genreHit + directorHit };
    });
    return scored
      .sort((a, b) => b.score - a.score || b.m.views - a.m.views)
      .slice(0, 4)
      .map((s) => s.m);
  }, [item]);

  if (!item) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col">
        <Navbar />
        <main className="flex-1 flex flex-col items-center justify-center text-center px-4 pt-24">
          <span className="w-16 h-16 rounded-full bg-background-200 flex items-center justify-center mb-4">
            <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
          </span>
          <p className="text-lg font-semibold text-foreground-900">未找到该影视作品</p>
          <p className="text-sm text-foreground-500 mt-1">作品可能已下架或链接有误</p>
          <Link
            to="/"
            className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap"
          >
            <i className="ri-home-line"></i>
            返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const fav = isFavorite(item.id);
  const heroBg = item.backdrop || item.poster;

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      showToast("链接已复制到剪贴板");
    } catch {
      showToast("复制失败，请手动复制地址栏链接");
    }
  };

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Navbar />
      <main className="flex-1">
        <section className="relative w-full overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={heroBg}
              alt={`${item.title} 背景剧照`}
              className="w-full h-full object-cover scale-105 blur-sm"
            />
            <div className="absolute inset-0 bg-foreground-950/80" />
          </div>

          <div className="relative z-10 w-full px-4 md:px-8 pt-24 md:pt-28 pb-10 md:pb-12">
            <nav className="text-sm text-background-300 mb-5 flex flex-wrap items-center gap-1.5">
              <Link to="/" className="hover:text-accent-400 transition-colors">
                首页
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link
                to={item.type === "movie" ? "/category/movie" : "/category/tv"}
                className="hover:text-accent-400 transition-colors"
              >
                {item.type === "movie" ? "电影" : "电视剧"}
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-background-100">{item.title}</span>
            </nav>

            <div className="flex flex-col sm:flex-row gap-6 md:gap-8">
              <div className="w-40 sm:w-52 md:w-60 shrink-0 mx-auto sm:mx-0">
                <div className="w-full aspect-[2/3] rounded-lg overflow-hidden border border-background-50/10">
                  <img
                    src={item.poster}
                    alt={`${item.title}（${item.genre.join("、")}）影视封面`}
                    title={`${item.title} - 光棍影院`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0 text-center sm:text-left">
                <div className="flex items-center gap-2 justify-center sm:justify-start mb-3">
                  <span className="px-2.5 py-1 rounded-md text-xs font-semibold bg-primary-500 text-background-50">
                    {item.type === "movie" ? "电影" : "电视剧"}
                  </span>
                  {item.tags?.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-1 rounded-md text-xs font-semibold bg-red-600 text-white"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <h1 className="text-2xl md:text-4xl font-bold text-background-50 font-heading">
                  {item.title}
                </h1>

                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-3 justify-center sm:justify-start text-sm text-background-300">
                  <span className="flex items-center gap-1 font-semibold text-accent-400">
                    <i className="ri-star-fill"></i>
                    {item.rating.toFixed(1)}
                  </span>
                  <span>{item.year}</span>
                  <span>{item.region}</span>
                  <span>{item.duration}</span>
                  <span className="px-2 py-0.5 rounded bg-background-50/10 text-background-100">
                    {item.status}
                  </span>
                </div>

                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5 text-sm max-w-2xl">
                  <p className="text-background-300">
                    <span className="text-background-500">导演：</span>
                    {item.director}
                  </p>
                  <p className="text-background-300">
                    <span className="text-background-500">主演：</span>
                    {item.cast.join("、")}
                  </p>
                  <p className="text-background-300">
                    <span className="text-background-500">类型：</span>
                    {item.genre.join("、")}
                  </p>
                  <p className="text-background-300">
                    <span className="text-background-500">更新：</span>
                    {item.updatedAt}
                  </p>
                </div>

                <div className="mt-7 flex flex-wrap items-center gap-3 justify-center sm:justify-start">
                  <button
                    type="button"
                    onClick={() => showToast("演示模式：暂未接入真实播放")}
                    className="inline-flex items-center gap-2 px-5 h-11 rounded-md bg-accent-500 text-foreground-950 text-sm font-semibold hover:bg-accent-400 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-play-fill text-lg"></i>
                    在线播放（演示）
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      toggle(item.id);
                      showToast(fav ? "已取消收藏" : "已加入收藏");
                    }}
                    className={`inline-flex items-center gap-2 px-5 h-11 rounded-md text-sm font-semibold transition-colors whitespace-nowrap ${
                      fav
                        ? "bg-red-600 text-white"
                        : "bg-background-50/15 text-background-50 hover:bg-background-50/25"
                    }`}
                  >
                    <i className={fav ? "ri-heart-fill" : "ri-heart-line"}></i>
                    {fav ? "已收藏" : "收藏"}
                  </button>
                  <button
                    type="button"
                    onClick={handleShare}
                    className="inline-flex items-center gap-2 px-5 h-11 rounded-md bg-background-50/15 text-background-50 text-sm font-semibold hover:bg-background-50/25 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-share-line"></i>
                    分享
                  </button>
                  <Link
                    to={item.type === "movie" ? "/category/movie" : "/category/tv"}
                    className="inline-flex items-center gap-2 px-5 h-11 rounded-md bg-background-50/15 text-background-50 text-sm font-semibold hover:bg-background-50/25 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-arrow-left-line"></i>
                    返回分类
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-50">
          <div className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-8">
            <h2 className="text-lg md:text-xl font-bold text-foreground-950 font-heading flex items-center gap-2 mb-4">
              <i className="ri-file-text-line text-primary-600"></i>
              剧情介绍
            </h2>
            <p
              className={`text-sm md:text-base text-foreground-700 leading-relaxed ${
                expanded ? "" : "line-clamp-3"
              }`}
            >
              {item.synopsis}
            </p>
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-accent-600 whitespace-nowrap"
            >
              {expanded ? "收起" : "展开阅读全文"}
              <i className={expanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
            </button>

            <div className="mt-6 pt-5 border-t border-background-200 flex items-start gap-2 text-xs text-foreground-500 leading-relaxed">
              <i className="ri-information-line text-base shrink-0"></i>
              <span>
                本站为内容展示演示，播放功能暂未接入真实接口，所有信息与评分均为示例数据，仅供参考。
              </span>
            </div>
          </div>
        </section>

        {related.length > 0 && (
          <section className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-100">
            <h2 className="text-lg md:text-xl font-bold text-foreground-950 font-heading flex items-center gap-2 mb-5">
              <i className="ri-magic-line text-accent-500"></i>
              相关推荐
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-5">
              {related.map((m) => (
                <MediaCard key={m.id} item={m} />
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />

      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[70] px-5 py-3 rounded-md bg-foreground-950 text-background-50 text-sm font-medium animate-fade-up whitespace-nowrap">
          {toast}
        </div>
      )}
    </div>
  );
}